/**
 * OverlayEditorErrorBoundary — catches failures in the lazy-loaded
 * StoryTextOverlayEditor chunk (e.g., network error during dynamic import).
 *
 * Shows a user-facing "Could not load the text editor. Try again." message
 * with a retry button that resets the boundary state.
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import { VStack, Text, Button } from '@chakra-ui/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class OverlayEditorErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[OverlayEditorErrorBoundary]', error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <VStack
          spacing={3}
          align="center"
          justify="center"
          py={12}
          px={4}
          bg="black"
          color="white"
        >
          <Text fontSize="sm" textAlign="center">
            Could not load the text editor. Try again.
          </Text>
          <Button
            size="sm"
            variant="outline"
            color="white"
            borderColor="whiteAlpha.500"
            onClick={this.handleRetry}
          >
            Retry
          </Button>
        </VStack>
      );
    }

    return this.props.children;
  }
}
